import type { UseFormReturnType } from "@mantine/form"

import {
  isLatitudeInRange,
  isLongitudeInRange,
} from "~/pages/Admin/components/Fields/coordinatesSchema"
import type { errorCodes } from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap"
import { getNestedValue } from "~/utils/types"

/**
 * Coordinates field hook: value updates + display errors computed from values.
 * Errors are computed directly (coherence on empty field when the other is filled;
 * range when filled and out of range). Form-level validation (zodResolver) still
 * runs on submit / isValid().
 */

type CoordinatesState = {
  latitude: number | null
  longitude: number | null
} | null

function parseCoordinateValue(value: string | number): number | null {
  if (value === "" || value === undefined) return null
  if (typeof value === "number" && !Number.isNaN(value)) return value
  const parsed = parseFloat(String(value))
  return Number.isNaN(parsed) ? null : parsed
}

function getCoordinates(
  form: UseFormReturnType<Record<string, unknown>>,
  path: string
): CoordinatesState {
  const value = getNestedValue(form.values, path)
  if (value === null || value === undefined) return null
  const coords =
    typeof value === "object" &&
    value !== null &&
    "latitude" in value &&
    "longitude" in value
      ? (value as { latitude?: unknown; longitude?: unknown })
      : null
  if (!coords) return null
  const lat = typeof coords.latitude === "number" ? coords.latitude : null
  const lon = typeof coords.longitude === "number" ? coords.longitude : null
  return { latitude: lat, longitude: lon }
}

function setCoordinates(
  form: UseFormReturnType<Record<string, unknown>>,
  path: string,
  state: CoordinatesState
): void {
  form.setFieldValue(path, state)
}

export type UseCoordinatesFieldParams = {
  form: UseFormReturnType<Record<string, unknown>>
  fieldName: string
  translations: errorCodes | undefined
  disabled?: boolean
}

export function useCoordinatesField({
  form,
  fieldName,
  translations,
  disabled = false,
}: UseCoordinatesFieldParams): {
  latitude: number | null
  longitude: number | null
  onLatitudeChange: (value: string | number) => void
  onLongitudeChange: (value: string | number) => void
  onBlur: () => void
  latitudeError: string | undefined
  longitudeError: string | undefined
  disabled: boolean
} {
  const coords = getCoordinates(form, fieldName)
  const latitude = coords?.latitude ?? null
  const longitude = coords?.longitude ?? null

  const latDefined = latitude !== null && latitude !== undefined
  const lonDefined = longitude !== null && longitude !== undefined

  let latitudeError: string | undefined
  let longitudeError: string | undefined

  if (!latDefined && lonDefined) {
    latitudeError = translations?.mustContainBothLatitudeAndLongitude
  }
  if (latDefined && !isLatitudeInRange(latitude)) {
    latitudeError = translations?.latitudeOutOfRange
  }

  if (!lonDefined && latDefined) {
    longitudeError = translations?.mustContainBothLatitudeAndLongitude
  }
  if (lonDefined && !isLongitudeInRange(longitude)) {
    longitudeError = translations?.longitudeOutOfRange
  }

  const onLatitudeChange = (value: string | number): void => {
    const nextLat = parseCoordinateValue(value)
    const current = getCoordinates(form, fieldName)
    const nextLon = current?.longitude ?? null
    const next: CoordinatesState =
      nextLat === null && nextLon === null
        ? null
        : { latitude: nextLat, longitude: nextLon }
    setCoordinates(form, fieldName, next)
  }

  const onLongitudeChange = (value: string | number): void => {
    const nextLon = parseCoordinateValue(value)
    const current = getCoordinates(form, fieldName)
    const nextLat = current?.latitude ?? null
    const next: CoordinatesState =
      nextLat === null && nextLon === null
        ? null
        : { latitude: nextLat, longitude: nextLon }
    setCoordinates(form, fieldName, next)
  }

  const onBlur = (): void => {
    // No-op; form validate runs on change. Kept for API compatibility (e.g. touch).
  }

  return {
    latitude,
    longitude,
    onLatitudeChange,
    onLongitudeChange,
    onBlur,
    latitudeError,
    longitudeError,
    disabled,
  }
}
